/**
 * DailyRoll — LOCKED CONTRACT: daily seed derivation (zero AI dependency).
 *
 * The day's challenge is a pure function of the calendar date in UTC. The
 * client derives the seed offline; the server derives the identical value.
 * No network call and no AI is required to know today's course.
 *
 * Owned by: Architect. Used by: Shell (boot), Core (generation), Backend.
 */

/** A UTC calendar day key, e.g. "2026-06-14". This is the unit of a daily run. */
export type DateKey = string;

/** UTC date key for the given instant (defaults to now). Format: YYYY-MM-DD. */
export function dateKeyOf(when: Date = new Date()): DateKey {
  const y = when.getUTCFullYear().toString().padStart(4, '0');
  const m = (when.getUTCMonth() + 1).toString().padStart(2, '0');
  const d = when.getUTCDate().toString().padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Derive the 32-bit unsigned numeric seed for a day. FNV-1a over the UTC date
 * key. Deterministic, dependency-free, and identical on client and server.
 */
export function deriveDailySeed(dateKey: DateKey): number {
  let hash = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < dateKey.length; i++) {
    hash ^= dateKey.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193); // FNV prime
  }
  return hash >>> 0;
}

/** Convenience: today's seed in UTC. */
export function todaysSeed(when: Date = new Date()): { dateKey: DateKey; seed: number } {
  const dateKey = dateKeyOf(when);
  return { dateKey, seed: deriveDailySeed(dateKey) };
}
